import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Chip,
  Autocomplete,
} from "@mui/material";
import { GridFilterItem, GridLogicOperator } from "@mui/x-data-grid-pro";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { customFilterFormStyles } from "./CustomFilterFormStyles";
import { TFunction } from "i18next";

interface CustomFilterFormProps {
  columns: any[];
  filterModel: {
    items: GridFilterItem[];
    logicOperator?: GridLogicOperator;
  };
  onFilterChange: (newFilterModel: {
    items: GridFilterItem[];
    logicOperator: GridLogicOperator;
  }) => void;
  onApplyFilters: () => void;
  onClose?: () => void;
  t: TFunction<"global", undefined>;
  preselectedField?: string | null;
  setPreselectedFilterField: (field: string | null) => void;
}

export const inarixExistingOperators = [
  { value: "$ilike", label: "contains" }, // contains
  { value: "$eq", label: "equals" }, // equals
  { value: "$gt", label: ">" }, // greater than
  { value: "$lt", label: "<" }, // less than
  { value: "$ne", label: "!=" }, // not equal
  { value: "$in", label: "is any of" },
];

const getColumnType = (columns: any[], field: string) => {
  const column = columns.find((col) => col.field === field);
  return column ? column.columnType : "";
};

const CustomFilterForm: React.FC<CustomFilterFormProps> = ({
  columns,
  filterModel,
  onFilterChange,
  onApplyFilters,
  onClose,
  t,
  preselectedField,
  setPreselectedFilterField,
}) => {
  const [filterItems, setFilterItems] = useState<any[]>([
    {
      field: "",
      operator: "",
      value: "",
      column: "",
      realOperator: "",
      columnType: "",
    },
  ]);
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});

  const formRef = React.useRef<HTMLDivElement>(null);

    const getTranslatedOperatorLabel = (operatorValue: string) => {
    switch (operatorValue) {
      case "$ilike":
        return t("custom_filter.operators.contains");
      case "$eq":
        return t("custom_filter.operators.equals");
      case "$gt":
        return t("custom_filter.operators.greater_than");
      case "$lt":
        return t("custom_filter.operators.less_than");
      case "$ne":
        return t("custom_filter.operators.not_equal");
      case "$in":
        return t("custom_filter.operators.is_any_of");
      default:
        return operatorValue;
    }
  };


  // Load current filter data on initialization
  useEffect(() => {
    if (filterModel?.items?.length > 0) {
      setFilterItems(filterModel.items);
    } else {
      setFilterItems([
        {
          field: "",
          operator: "",
          value: "",
          column: "",
          realOperator: "",
          columnType: "",
        },
      ]);
    }
  }, [filterModel]);

  useEffect(() => {
    if (preselectedField) {
      setFilterItems((prev) => {
        // Check if there are existing valid filters
        const hasExistingFilters = prev.some(
          (item) => item.field && item.field !== ""
        );

        const columnType = getColumnType(columns, preselectedField);

        if (!hasExistingFilters && prev.length === 1) {
          return [
            {
              field: preselectedField,
              operator: "",
              value: "",
              column: "",
              realOperator: "",
              columnType: columnType,
            },
          ];
        } else {
          return [
            ...prev,
            {
              field: preselectedField,
              operator: "",
              value: "",
              column: "",
              realOperator: "",
              columnType: columnType,
            },
          ];
        }
      });

      setPreselectedFilterField(null);
    }
  }, [preselectedField, setPreselectedFilterField]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (formRef.current && formRef.current.contains(target)) {
        return;
      }

      const isInMuiPopover =
        target &&
        (target as Element).closest(
          ".MuiPopover-root, .MuiMenu-root, .MuiSelect-popper, .MuiAutocomplete-popper"
        );
      if (isInMuiPopover) {
        return;
      }

      onClose?.();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleFieldChange = (index: number, field: string) => {
    const newItems = [...filterItems];
    newItems[index] = {
      ...newItems[index],
      field,
      operator: "",
      value: "",
      column: "",
      realOperator: "",
      columnType: getColumnType(columns, field),
    };
    setFilterItems(newItems);
  };

  const handleOperatorChange = (index: number, operator: string) => {
    // Update the operator for the specific filter item
    const newItems = [...filterItems];
    newItems[index] = {
      ...newItems[index],
      operator,
      realOperator: operator,
      column:
        columns.find((col) => col.field === newItems[index].field)
          ?.headerName || "",
      columnType: getColumnType(columns, newItems[index].field),
    };
    setFilterItems(newItems);
  };

  const handleValueChange = (index: number, value: string) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value };
    setFilterItems(newItems);
  };

  const handleChipValueChange = (index: number, values: string[]) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value: values.join(",") };
    setFilterItems(newItems);
  };

  const getChipValues = (value: string) => {
    return value ? value.split(",").map(v => v.trim()).filter(Boolean) : [];
  };

  const getAvailableOperators = (fieldName: string) => {
    if (!fieldName) return [];
    const column = columns.find((col) => col.field === fieldName);
    // Return available filter operators for the selected field
    return column?.filterOperators || [];
  };

  const addFilter = () => {
    setFilterItems([
      ...filterItems,
      {
        field: "",
        operator: "",
        value: "",
        column: "",
        realOperator: "",
        columnType: "",
      },
    ]);
  };

  const removeFilter = (index: number) => {
    const newItems = filterItems.filter((_, i) => i !== index);
    setFilterItems(
      newItems.length > 0
        ? newItems
        : [
            {
              field: "",
              operator: "",
              value: "",
              column: "",
              realOperator: "",
              columnType: "",
            },
          ]
    );
  };

  const handleApplyFilter = () => {
    const validItems = filterItems.filter(
      (item) => item.field && item.operator
    );

    const newFilterModel = {
      items: validItems,
      logicOperator: GridLogicOperator.And,
    };

    onFilterChange(newFilterModel);
    onApplyFilters();
  };

  const handleClearAll = () => {
    setFilterItems([
      {
        field: "",
        operator: "",
        value: "",
        column: "",
        realOperator: "",
        columnType: "",
      },
    ]);

    const newFilterModel = {
      items: [],
      logicOperator: GridLogicOperator.And,
      column: "",
      realOperator: "",
    };

    onFilterChange(newFilterModel);
    onApplyFilters();
  };

  const getFilterableColumns = () => {
    return columns.filter(
      (col) => col.filterable !== false && col.filterOperators?.length > 0
    );
  };

  return (
    <Box
      ref={formRef}
      onClick={(e) => e.stopPropagation()}
      sx={customFilterFormStyles.container}
    >
      {/* Header row */}
      <Box sx={customFilterFormStyles.headerRow}>
        <Box></Box>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.columns")}
        </Typography>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.operator")}
        </Typography>
        <Typography variant="body2" sx={customFilterFormStyles.headerText}>
          {t("custom_filter.value")}
        </Typography>
      </Box>

      {filterItems.map((filterItem, index) => (
        <Box key={`filter-${index}-${filterItem.field}`} sx={customFilterFormStyles.filterRow}>
          <Button
            onClick={() => removeFilter(index)}
            disabled={filterItems.length === 0}
            sx={customFilterFormStyles.deleteButton}
          >
            ✕
          </Button>

          <Select
            value={filterItem.field}
            onChange={(e) => handleFieldChange(index, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            displayEmpty
            size="small"
            variant="standard"
            sx={customFilterFormStyles.select}
          >
            <MenuItem value="">
              <em style={customFilterFormStyles.placeholderText}>
                {t("custom_filter.select_field")}
              </em>
            </MenuItem>
            {getFilterableColumns().map((column) => (
              <MenuItem key={column.field} value={column.field}>
                {column.headerName || column.field}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={filterItem.operator || ""}
            onChange={(e) => handleOperatorChange(index, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            disabled={!filterItem.field}
            displayEmpty
            size="small"
            variant="standard"
            sx={customFilterFormStyles.select}
          >
            <MenuItem value="">
              <em style={customFilterFormStyles.placeholderText}>
                {t("custom_filter.operator")}
              </em>
            </MenuItem>
            {getAvailableOperators(filterItem.field).map((operator) => (
              <MenuItem key={operator.value} value={operator.value}>
                {getTranslatedOperatorLabel(operator.value)}
              </MenuItem>
            ))}
          </Select>

          {filterItem.realOperator === "$in" ? (
            <Autocomplete
              multiple
              freeSolo
              options={inputValues[index] && inputValues[index].trim() ? [inputValues[index].trim()] : []}
              value={getChipValues(filterItem.value)}
              inputValue={inputValues[index] || ""}
              onInputChange={(event, newInputValue) => {
                setInputValues(prev => ({ ...prev, [index]: newInputValue }));
              }}
              onChange={(event, newValue) => {
                event?.stopPropagation();
                handleChipValueChange(index, newValue);
                setInputValues(prev => ({ ...prev, [index]: "" }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index: tagIndex })}
                    key={`${option}-${tagIndex}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={t("custom_filter.filter_value")}
                  size="small"
                  disabled={!filterItem.realOperator}
                  sx={customFilterFormStyles.textField}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              componentsProps={{
                popper: {
                  onClick: (e: React.MouseEvent) => e.stopPropagation()
                }
              }}
              sx={{ width: "100%" }}
            />
          ) : (
            <TextField
              placeholder={t("custom_filter.filter_value")}
              value={filterItem.value}
              variant="standard"
              onChange={(e) => handleValueChange(index, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              size="small"
              disabled={!filterItem.operator}
              sx={customFilterFormStyles.textField}
            />
          )}
        </Box>
      ))}

      {/* Action buttons */}
      <Box sx={customFilterFormStyles.actionButtonsContainer}>
        <Button
          startIcon={<span style={customFilterFormStyles.iconStyle}>+</span>}
          sx={customFilterFormStyles.actionButton}
          onClick={addFilter}
        >
          {t("custom_filter.add_filter")}
        </Button>

        <Button
          startIcon={
            <span style={customFilterFormStyles.iconStyle}>
              <DeleteForeverIcon fontSize="small" />
            </span>
          }
          sx={customFilterFormStyles.actionButton}
          onClick={handleClearAll}
        >
          {t("custom_filter.remove_all")}
        </Button>
        {/* Apply Filter Button */}
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          sx={customFilterFormStyles.applyButton}
        >
          {t("Common_options.Apply")}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomFilterForm;
